import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, FileCheck2, AlertCircle, X, Filter } from "lucide-react";

export default function AdminUpload() {
  const [files, setFiles] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [selected, setSelected] = useState([]); // author ids
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/authors").then(({ data }) => setAuthors(data));
  }, []);

  const toggleAuthor = (id) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const onSubmit = async () => {
    if (files.length === 0) return;
    setBusy(true); setErr(""); setResults(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      if (selected.length) fd.append("whitelist_author_ids", selected.join(","));
      const { data } = await api.post("/admin/reports/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data.results || []);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-10" data-testid="admin-upload-page">
      <header>
        <p className="hh-overline mb-3">Admin · Upload Reports</p>
        <h1 className="font-serif text-4xl text-hh-ink tracking-tight">Upload IngramSpark compensation PDFs</h1>
        <p className="mt-4 text-hh-ink2 max-w-2xl">
          Drop in your monthly compensation reports. We auto-detect report type, period and currency from the filename, then ingest every line item.
          Re-uploading the same file is safe — duplicates are skipped automatically.
        </p>
      </header>

      {/* Optional author whitelist */}
      <section className="hh-card p-6 md:p-7" data-testid="upload-whitelist">
        <div className="flex items-center gap-3 mb-2">
          <span className="h-9 w-9 rounded-full bg-hh-muted grid place-items-center">
            <Filter size={16} strokeWidth={1.5} className="text-hh-primary" />
          </span>
          <div>
            <h3 className="font-serif text-lg text-hh-ink">Limit ingestion to specific authors <span className="text-xs text-hh-ink2 font-sans">(optional)</span></h3>
            <p className="text-xs text-hh-ink2">Useful when an IngramSpark report contains rows for books that aren&rsquo;t yet contracted with Healing House — only the authors you pick will be ingested. Leave empty to ingest everything.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {authors.map((a) => {
            const on = selected.includes(a.id);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggleAuthor(a.id)}
                data-testid={`whitelist-${a.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm border transition-all ${
                  on
                    ? "bg-hh-primary text-white border-hh-primary"
                    : "bg-white text-hh-ink border-hh-border hover:border-hh-primary/40"
                }`}
              >
                {a.name}
                {on && <X size={12} strokeWidth={2} className="ml-1.5 inline -translate-y-px" />}
              </button>
            );
          })}
        </div>
        {selected.length > 0 && (
          <div className="mt-4 text-xs text-hh-primary bg-hh-primary/[0.06] border border-hh-primary/20 rounded-lg px-3 py-2 inline-block">
            Only {selected.length} author{selected.length === 1 ? "" : "s"} will be ingested.
            Rows for everyone else in the PDFs will be reported as <em>filtered out</em>.
          </div>
        )}
      </section>

      {/* Upload */}
      <div className="hh-card p-10">
        <label htmlFor="file-input" className="block cursor-pointer">
          <div className="border-2 border-dashed border-hh-border rounded-xl p-12 text-center hover:border-hh-primary transition-colors">
            <Upload size={32} strokeWidth={1.2} className="text-hh-primary mx-auto mb-4" />
            <p className="font-serif text-xl text-hh-ink">
              {files.length > 0 ? `${files.length} file${files.length === 1 ? "" : "s"} ready` : "Drop PDF reports here or click to browse"}
            </p>
            <p className="text-xs text-hh-ink2 mt-2">PDF only · multiple files allowed</p>
          </div>
        </label>
        <input
          id="file-input"
          ref={inputRef}
          type="file"
          accept="application/pdf"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
          className="hidden"
          data-testid="upload-file-input"
        />
        {files.length > 0 && (
          <ul className="mt-6 space-y-2 text-sm text-hh-ink">
            {files.map((f) => (
              <li key={`${f.name}-${f.size}-${f.lastModified}`} className="font-mono text-xs">· {f.name} ({Math.round(f.size / 1024)} KB)</li>
            ))}
          </ul>
        )}
        <div className="mt-6 flex justify-end gap-3">
          {files.length > 0 && (
            <button onClick={() => { setFiles([]); if (inputRef.current) inputRef.current.value = ""; }} className="hh-btn-ghost">Clear</button>
          )}
          <button onClick={onSubmit} disabled={busy || files.length === 0} className="hh-btn-primary disabled:opacity-50" data-testid="upload-submit">
            {busy ? "Ingesting…" : `Ingest ${files.length || ""} report${files.length === 1 ? "" : "s"}`}
          </button>
        </div>
        {err && (
          <div className="mt-4 text-sm text-hh-danger flex items-center gap-2"><AlertCircle size={14} /> {err}</div>
        )}
      </div>

      {results && (
        <div className="hh-card overflow-hidden" data-testid="upload-results">
          <div className="px-6 py-4 bg-hh-muted border-b border-hh-border">
            <h3 className="font-serif text-xl text-hh-ink">Ingestion results</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left bg-white">
              <tr>
                <th className="px-6 py-3 hh-overline">File</th>
                <th className="px-6 py-3 hh-overline">Rows</th>
                <th className="px-6 py-3 hh-overline">Inserted</th>
                <th className="px-6 py-3 hh-overline">Skipped (duplicates)</th>
                <th className="px-6 py-3 hh-overline">Filtered out</th>
                <th className="px-6 py-3 hh-overline">Status</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.file} className="border-t border-hh-border">
                  <td className="px-6 py-3 font-mono text-xs text-hh-ink">{r.file}</td>
                  <td className="px-6 py-3 text-hh-ink2">{r.rows ?? "—"}</td>
                  <td className="px-6 py-3 text-hh-ink font-medium">{r.inserted ?? "—"}</td>
                  <td className="px-6 py-3 text-hh-ink2">{r.skipped ?? "—"}</td>
                  <td className="px-6 py-3 text-hh-ink2">{r.filtered_out ?? "—"}</td>
                  <td className="px-6 py-3">
                    {r.ok ? (
                      <span className="text-hh-sage inline-flex items-center gap-1"><FileCheck2 size={14} /> OK</span>
                    ) : (
                      <span className="text-hh-danger inline-flex items-center gap-1"><AlertCircle size={14} /> {r.error}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
